import {
  CapacitorSQLite,
  SQLiteConnection,
  type SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

const DB_NAME = 'event_people_directory';
const sqliteConnection = new SQLiteConnection(CapacitorSQLite);

const SCHEMA = `
CREATE TABLE IF NOT EXISTS events (
  id TEXT PRIMARY KEY,
  payload TEXT NOT NULL,
  synced_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS people (
  id TEXT PRIMARY KEY,
  payload TEXT NOT NULL,
  synced_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS favorites (
  id TEXT PRIMARY KEY,
  type TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS sync_queue (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  action TEXT NOT NULL,
  entity TEXT NOT NULL,
  payload TEXT NOT NULL,
  created_at TEXT NOT NULL
);
`;

class SqliteService {
  private db: SQLiteDBConnection | null = null;

  /** Browser caching uses localStorage; native platforms initialize SQLite here. */
  async init(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    this.db = await sqliteConnection.createConnection(DB_NAME, false, 'no-encryption', 1, false);
    await this.db.open();
    await this.db.execute(SCHEMA);
  }

  private ensureDb(): SQLiteDBConnection {
    if (!this.db) throw new Error('SQLite not initialized. Call sqliteService.init() first.');
    return this.db;
  }

  async upsertEntity(table: 'events' | 'people', id: string, payload: unknown): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      const records = this.readWebCache<Record<string, unknown>>(table);
      records[id] = payload;
      localStorage.setItem(this.webCacheKey(table), JSON.stringify(records));
      return;
    }
    const db = this.ensureDb();
    await db.run(
      `INSERT INTO ${table} (id, payload, synced_at) VALUES (?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET payload = excluded.payload, synced_at = excluded.synced_at;`,
      [id, JSON.stringify(payload), new Date().toISOString()],
    );
  }

  async getAll<T>(table: 'events' | 'people'): Promise<T[]> {
    if (Capacitor.getPlatform() === 'web') {
      return Object.values(this.readWebCache<Record<string, T>>(table));
    }
    const db = this.ensureDb();
    const result = await db.query(`SELECT payload FROM ${table};`);
    return (result.values ?? []).map((row) => JSON.parse(row.payload) as T);
  }

  async queueMutation(action: 'create' | 'update' | 'delete', entity: string, payload: unknown) {
    const db = this.ensureDb();
    await db.run(
      `INSERT INTO sync_queue (action, entity, payload, created_at) VALUES (?, ?, ?, ?);`,
      [action, entity, JSON.stringify(payload), new Date().toISOString()],
    );
  }

  async drainQueue(): Promise<
    Array<{ id: number; action: string; entity: string; payload: unknown }>
  > {
    const db = this.ensureDb();
    const result = await db.query(`SELECT * FROM sync_queue ORDER BY id ASC;`);
    return (result.values ?? []).map((row) => ({
      ...row,
      payload: JSON.parse(row.payload),
    }));
  }

  async clearQueueItem(id: number): Promise<void> {
    const db = this.ensureDb();
    await db.run(`DELETE FROM sync_queue WHERE id = ?;`, [id]);
  }

  private webCacheKey(table: 'events' | 'people') {
    return `${DB_NAME}_${table}`;
  }

  private readWebCache<T>(table: 'events' | 'people'): T {
    const cached = localStorage.getItem(this.webCacheKey(table));
    return (cached ? JSON.parse(cached) : {}) as T;
  }
}

export const sqliteService = new SqliteService();
